<template>
  <v-container class="pa-0" height="100%">
    <CardTemplate v-if="$vuetify.display.smAndUp" @show-modal="showDialog">
      <template #icon>
        <v-img
          class="account-img"
          src="./assets/update-info.svg"
          alt=""
          role="presentation"
          width="46px"
          height="46px"
        />
      </template>
      <template #title>
        {{ $t('L_mobile_phone_number') }}
      </template>
      <template v-if="mobileNoExists" #content>
        <p
          :class="
            !$vuetify.display.xs
              ? 'd-flex align-center ga-2 justify-center mt-4'
              : 'd-block text-left mt-0 ml-2 pl-16'
          "
        >
          <span v-if="customerProfile">
            {{ formatedContactNumber }}
            <span v-if="!customerProfile || !mobileNoVerified">&nbsp; &ndash; </span>
          </span>
          <a
            @click.stop="verifyNow"
            id="verify_now"
            :aria-label="$t('L_verify_now')"
            href="javascript: void(0)"
            v-if="!mobileNoVerified"
          >
            {{ $t('L_verify_now') }}
          </a>
        </p>
        <p
          v-html="bindVerifiedLabel"
          class="font-weight-regular font-italic"
          :class="
            !$vuetify.display.xs
              ? 'd-flex align-center ga-2 justify-center'
              : 'd-block text-left mt-0 ml-2 pb-4 pl-16'
          "
        ></p>
      </template>
      <template v-else #content>
        <p
          class="content"
          :class="
            !$vuetify.display.xs
              ? 'd-flex align-center justify-center mt-4'
              : 'd-block text-left mt-0 ml-2 pb-4 pl-14'
          "
        >
          {{ $t('L_to_keep_you_account') }}
        </p>
      </template>
      <template #action>
        <OnexButton
          v-if="mobileNoExists"
          @click="((showUpdatePhoneDialog = true), (updatedNumber = false))"
          variant="text"
          id="update_user_mobile_trigger"
          :aria-label="$t('L_update_mobile_number')"
        >
          {{ $t('L_edit') }}
        </OnexButton>
        <OnexButton
          v-else
          @click="((showUpdatePhoneDialog = true), (updatedNumber = false))"
          variant="text"
          id="add_mobile_phone"
          :aria-label="$t('L_add_your_mobile_phone')"
        >
          {{ $t('L_add_your_mobile_phone') }}
        </OnexButton>
      </template>
    </CardTemplate>

    <UpdateContactMobile
      v-else
      @show-modal="showDialog"
      @verify-now="verifyNow"
      :formated-contact-number="formatedContactNumber"
      :mobile-no-exists="mobileNoExists"
      :mobile-no-verified="mobileNoVerified"
      :bind-verified-label="bindVerifiedLabel"
      :customer-profile="customerProfile"
    />
    <MyAccountDialog
      :header="$t('L_update_mobile_number')"
      :show="showUpdatePhoneDialog"
      @save="next()"
      @close="
        ((phoneNumber = ''),
        (showUpdatePhoneDialog = false),
        (closeIsActive = false),
        verifyPhone())
      "
      :close-is-active="closeIsActive"
      :enable-save-button="enableSaveButton"
      :submit-btn-text="$t('L_next')"
    >
      <v-row>
        <v-col cols="12" font-size--16 text-xs-center mb-4>
          {{ $t('L_this_phone_number_may_be_used_for_verification_purposes') }}
        </v-col>
        <v-col cols="12">
          <OnexLabelTooltip
            class="mb-0 font-size--12 font-weight-bold"
            style="display: block"
            :label="$t('L_current_mobile_number')"
          />

          <OnexLabelTooltip
            v-if="customerProfile"
            class="mb-5"
            style="display: block; color: #767575"
            :label="formatedContactNumber"
          />
        </v-col>
        <v-col cols="12">
          <v-form id="update-contact-form" ref="form" v-model="validForm">
            <OnexPhone
              data-comp-id="mobile-phone"
              :disabled="updatedNumber"
              v-model="phoneNumber"
              :label="$t('L_step_1_phone')"
              :rules="[v$.phoneNumber.notCurrentNumber.$response]"
              required
            >
              <template #append-inner>
                <v-icon v-if="enableSaveButton" color="success" :icon="mdiCheckCircle" />
              </template>
            </OnexPhone>
          </v-form>
        </v-col>
      </v-row>
    </MyAccountDialog>
  </v-container>
</template>
<script lang="ts">
import { required } from '@vuelidate/validators';
import { phoneNumber as phoneNumberFormatter } from '@/shared/util/formatters';
import { defineComponent, type ComponentOptions } from 'vue';
import { mapActions, mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { useVuelidate } from '@vuelidate/core';
import type { ICustomerStoreCustomerProfile } from '@/stores/customer/customerStore.model';
import bff from '@/shared/api/bff';
import UpdateContactMobile from './UpdateContactMobile.vue';
import MyAccountDialog from './MyAccountDialog.vue';
import CardTemplate from './CardTemplate.vue';
import { OnexButton, OnexLabelTooltip, OnexPhone } from '@primerica/onex-ui-lib';
import { $t } from '@/i18n/i18n';
import { mdiCheckCircle } from '@mdi/js';

export const notCurrentNumberValidator = (
  value: string,
  vm: { customerProfile: { mobilePhone?: string } }
) => value !== vm.customerProfile.mobilePhone?.trim() || $t('L_mobile_number_must_be_different');

const componentOptions: ComponentOptions = {
  name: 'UpdateContact',
  components: {
    CardTemplate,
    MyAccountDialog,
    UpdateContactMobile,
    OnexButton,
    OnexLabelTooltip,
    OnexPhone,
  },
  data: () => ({
    showUpdatePhoneDialog: false,
    phoneNumber: '',
    closeIsActive: false,
    updatedNumber: false,
    clickHandler: Event,
    v$: useVuelidate(),
    validForm: false,
    mdiCheckCircle,
  }),
  validations() {
    return {
      phoneNumber: {
        required,
        notCurrentNumber: notCurrentNumberValidator,
      },
    };
  },
  computed: {
    ...mapStores(useGlobalStore, useCustomerStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(useGlobalStore, {
      containsFeature: 'containsFeature',
    }),
    formatedContactNumber() {
      if (this.customerProfile.mobilePhone?.trim()) {
        return phoneNumberFormatter(this.customerProfile.mobilePhone);
      }
      return '-';
    },
    enableSaveButton() {
      return this.validForm && !this.v$.$invalid && !this.v$.phoneNumber.$invalid;
    },
    mobileNoExists() {
      return !!(
        this.customerProfile.mobilePhone !== '' && this.customerProfile.mobilePhone !== '1231231234'
      );
    },
    mobileNoVerified() {
      return !!this.customerProfile.phoneVerified;
    },
    bindVerifiedLabel() {
      if (this.customerProfile.phoneVerified) {
        return this.$t('L_phoneno_verified');
      }
      return this.$t('L_phoneno_unverified');
    },
  },
  watch: {
    mobileNoVerified() {
      this.setClickEvent();
    },
  },
  methods: {
    ...mapActions(useCustomerStore, ['updateCustomerProfile']),
    verifyNow() {
      this.showUpdatePhoneDialog = false;
      this.closeIsActive = false;
      this.updatedNumber = true;
      this.verifyPhone();
    },
    showDialog() {
      this.showUpdatePhoneDialog = true;
    },
    setClickEvent() {
      const clickTarget = this.$el.querySelector('.clickable');
      if (
        clickTarget &&
        ((this.mobileNoVerified && this.$vuetify.display.smAndUp) || this.$vuetify.display.xs)
      ) {
        this.clickHandler = () => this.showDialog();
        clickTarget.addEventListener('click', this.clickHandler);
      } else if (clickTarget) {
        clickTarget.removeEventListener('click', this.clickHandler);
      }
    },
    async next() {
      this.v$.$reset();
      // this.phoneNumber = '';
      this.closeIsActive = true;
      // Call Verify, In the MyAccount passing true flag for :bypass-initial-page-and-send-otp
      await this.$emit('verify-bypass-page-one', this.phoneNumber);
      this.phoneNumber = '';
      this.showUpdatePhoneDialog = false;
    },

    // Actual save of data to backend
    async save() {
      const mobileInfo = {
        mobileNumber: this.phoneNumber,
      };
      try {
        /*
         * Emit a custom action when a user successfully submits an update mobile number request
         * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
         */
        const i = this.$dynatrace.enterAction('account_settings_changes_saved_mobile_number');
        await bff.updateMobileNumber(mobileInfo);
        this.$dynatrace.leaveAction(i);
        this.globalStore.addAlert({
          type: 'success',
          details: { message: this.$t('L_MOBILE_VERIFICATION_Mobile_Number_Successfully_Updated') },
          context: 'myaccount',
        });
        this.updateCustomerProfile({
          mobilePhone: this.phoneNumber,
          phoneVerified: false,
        } as ICustomerStoreCustomerProfile);
        this.v$.$reset();
        this.phoneNumber = '';
        this.closeIsActive = true;
        this.updatedNumber = true;
      } catch (e) {
        this.globalStore.addError({ error: e, context: 'myaccount' });
      }
    },
    verifyPhone() {
      if (this.updatedNumber) {
        this.$emit('verify-go-to-page-one', this.customerProfile.mobilePhone);
        this.updatedNumber = false;
      }
    },
  },
  mounted() {
    this.setClickEvent();
    this.phoneNumber = '';
  },
};

export default defineComponent(componentOptions);
</script>
<style lang="scss">
#label-tooltip .label .v-icon__svg {
  display: none !important;
}
</style>
